import { useState } from 'react';
import { FileText, ChevronDown } from 'lucide-react';
import { useTelegram } from '@/hooks/useTelegram';
import { LegalDocumentViewer } from '@/components/LegalDocumentViewer';
import { LEGAL_DOCS_LIST, LEGAL_DOCUMENT_VERSION, type LegalDocument } from '@/legal';

/**
 * Карточка «Юридические документы» в Профиле
 * При раскрытии — список всех 4 документов с возможностью просмотра
 */
export function LegalDocumentsCard() {
  const { haptic } = useTelegram();
  const [expanded, setExpanded] = useState(false);
  const [viewingDoc, setViewingDoc] = useState<LegalDocument | null>(null);

  function toggleExpand() {
    haptic('light');
    setExpanded(!expanded);
  }

  function openDoc(doc: LegalDocument) {
    haptic('light');
    setViewingDoc(doc);
  }

  return (
    <>
      <div className="card overflow-hidden mb-5">
        <button
          onClick={toggleExpand}
          className="w-full p-3 flex items-center gap-3 hover:bg-bg-elevated/50 transition-colors"
        >
          <div className="w-9 h-9 rounded-xl bg-ink-muted/10 flex items-center justify-center shrink-0">
            <FileText size={16} className="text-ink-muted" />
          </div>
          <div className="flex-1 text-left">
            <div className="font-display font-semibold text-sm">Юридические документы</div>
            <div className="text-[11px] text-ink-dim">
              Оферта, политика, согласия
            </div>
          </div>
          <ChevronDown 
            size={16} 
            className={`text-ink-dim transition-transform ${expanded ? 'rotate-180' : ''}`} 
          />
        </button>

        {expanded && (
          <div className="px-3 pb-3 pt-1 border-t border-line/30 bg-bg-elevated/30">
            <div className="space-y-2 pt-2">
              {LEGAL_DOCS_LIST.map((doc) => (
                <button
                  key={doc.key}
                  type="button"
                  onClick={() => openDoc(doc)}
                  className="w-full p-2.5 rounded-lg bg-bg border border-line/40 hover:border-emerald-glow/30 hover:bg-emerald-glow/5 active:scale-[0.99] transition-all flex items-center gap-3 text-left"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{doc.shortTitle}</div>
                    <div className="text-[10px] text-ink-muted leading-tight truncate">
                      {doc.description}
                    </div>
                  </div>
                  <span className="text-emerald-glow text-xs shrink-0">→</span>
                </button>
              ))}
            </div>

            <div className="mt-3 pt-2 border-t border-line/30 text-center">
              <p className="text-[10px] text-ink-dim">
                Действующая редакция: {LEGAL_DOCUMENT_VERSION}
              </p>
            </div>
          </div>
        )}
      </div>

      <LegalDocumentViewer
        document={viewingDoc}
        onClose={() => setViewingDoc(null)}
      />
    </>
  );
}
